import { createE2eRelayNetworkAdapter } from './e2eRelayNetworkAdapter.js';
import { createDeChessPeerJSNetworkAdapter } from './peerJsNetworkAdapter.js';

const configuredSignalingUrls = (import.meta.env?.VITE_SIGNALING_URLS || '')
  .split(',')
  .map((url) => url.trim())
  .filter(Boolean);
const e2eRelayUrl = (import.meta.env?.VITE_E2E_RELAY_URL || '').trim();

/**
 * Single PeerJS signaling host for every client.
 * Peer IDs are server-scoped: silent failover to a second host splits the lobby
 * (Online now still works via PeerBasket, but WebRTC challenges cannot connect).
 * 0.peerjs.com is avoided as the default — it has been blocking dechess.org clients.
 * Override with VITE_SIGNALING_URLS only when every client shares the same list.
 */
export const DEFAULT_SIGNALING_URLS = Object.freeze([
  'wss://dechess-org-peerjs.vercel.app/peerjs?key=peerjs'
]);

export const SIGNALING_URLS = configuredSignalingUrls.length > 0
  ? configuredSignalingUrls
  : [...DEFAULT_SIGNALING_URLS];

/**
 * ICE servers so PeerJS data channels can form across NATs.
 */
export const DEFAULT_ICE_SERVERS = Object.freeze([
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun.relay.metered.ca:80' },
  {
    urls: 'turn:openrelay.metered.ca:80',
    username: 'openrelayproject',
    credential: 'openrelayproject'
  },
  {
    urls: 'turn:openrelay.metered.ca:443',
    username: 'openrelayproject',
    credential: 'openrelayproject'
  },
  {
    urls: 'turn:openrelay.metered.ca:443?transport=tcp',
    username: 'openrelayproject',
    credential: 'openrelayproject'
  },
  {
    urls: 'turns:openrelay.metered.ca:443?transport=tcp',
    username: 'openrelayproject',
    credential: 'openrelayproject'
  }
]);

/** Shared lobby discovery room for all time controls and variants. */
export const LOBBY_SCOPE = 'lobby:global';
/** dignity.js PeerGroup for lobby chat / room gossip (high-fanout pubsub). */
export const LOBBY_CHAT_PEER_GROUP = 'dechess:lobby:chat';

// This is a public protocol namespace and spam gate, not an encryption secret.
const APP_PASSWORD = 'dechess.org:v0.1:lobby';

function createNetworkAdapter() {
  if (e2eRelayUrl) {
    return createE2eRelayNetworkAdapter({ url: e2eRelayUrl });
  }

  // Keep the dechess adapter: it pins the Vercel host and a longer retry
  // schedule. dignity.js 1.1.1 no longer destroys the Peer on dial errors,
  // but its default hosts are peerjs.92k.de and 0.peerjs.com.
  return createDeChessPeerJSNetworkAdapter({
    urls: SIGNALING_URLS,
    // Fail faster on dead sockets / taken ids so lobby hard-refresh is snappy.
    connectTimeoutMs: 5_000,
    iceServers: [...DEFAULT_ICE_SERVERS],
    startAttempts: 3,
    startRetryDelaysMs: [200, 500]
  });
}

export function chatBroadcastPassword(roomId = 'main') {
  return `${APP_PASSWORD}:chat:${String(roomId || 'main')}`;
}

/**
 * Prefer a lighter PoW budget on phones / coarse-pointer / low-memory clients.
 * dignity.js calibrates step count once to hit powTargetMs on this device.
 */
export function isConstrainedClient(env = globalThis) {
  const nav = env?.navigator;
  if (!nav) {
    return false;
  }
  if (nav.userAgentData?.mobile === true) {
    return true;
  }
  if (nav.connection?.saveData === true) {
    return true;
  }
  if (typeof nav.deviceMemory === 'number' && nav.deviceMemory > 0 && nav.deviceMemory <= 4) {
    return true;
  }
  const coarse = env.matchMedia?.('(pointer: coarse)');
  if (coarse?.matches && typeof nav.maxTouchPoints === 'number' && nav.maxTouchPoints > 1) {
    return true;
  }
  return false;
}

/**
 * dignity.js DEFAULT_SECURITY_OPTIONS.powSteps (22) ignores powTargetMs.
 * Pass powSteps: null so MessageSecurityService.determinePowSteps() probes
 * this browser and scales VDF steps to the wall-clock budget below.
 */
export function createPowSecurityOptions(env = globalThis) {
  const constrained = isConstrainedClient(env);
  return {
    powEnabled: true,
    powSteps: null,
    // Moves must clear the wire quickly; keep PBKDF2 at dignity default so
    // mixed client versions can still decrypt each other during rollouts.
    powTargetMs: constrained ? 8 : 12
  };
}

export function createNodeConfig(identity, env = globalThis) {
  return {
    nodeId: identity.peerId,
    networkAdapter: createNetworkAdapter(),
    security: {
      appPassword: APP_PASSWORD,
      broadcastPasswords: {
        [LOBBY_SCOPE]: APP_PASSWORD,
        'lobby:chat:main': chatBroadcastPassword('main'),
        [`gossip:${LOBBY_CHAT_PEER_GROUP}`]: APP_PASSWORD,
        default: APP_PASSWORD
      },
      resolveBroadcastScope: () => LOBBY_SCOPE,
      resolveBroadcastPassword: ({ scope } = {}) => {
        const value = String(scope || '');
        if (value.startsWith('lobby:chat:')) {
          const roomId = value.slice('lobby:chat:'.length) || 'main';
          return chatBroadcastPassword(roomId);
        }
        return APP_PASSWORD;
      },
      ...createPowSecurityOptions(env),
      discoveryHeartbeatMs: 4000,
      presenceTtlMs: 45000,
      globalMaxOpenConnections: 48,
      keyPair: identity.keyPair
    }
  };
}
